import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGame } from '../state/GameProvider';

const PERSIST_KEY = 'share-paint:identity';
const hasIdentity = () =>
  typeof localStorage !== 'undefined' && !!localStorage.getItem(PERSIST_KEY);

export function Lobby() {
  const { code } = useParams<{ code: string }>();
  const nav = useNavigate();
  const { state, startGame } = useGame();

  useEffect(() => {
    if (state.status === 'PLAYING' && code) {
      nav(`/game/${code}`, { replace: true });
    } else if (state.status === 'VOTING' && code) {
      nav(`/vote/${code}`, { replace: true });
    } else if (state.status === 'REVEAL' && code) {
      nav(`/reveal/${code}`, { replace: true });
    }
  }, [state.status, nav, code]);

  // Si tras 2.5s seguimos sin sesión NI identidad persistida → volver al inicio.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!state.session && !hasIdentity()) nav('/', { replace: true });
    }, 2500);
    return () => clearTimeout(t);
  }, [state.session, nav]);

  if (!state.session || !state.me) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60">
        Conectando a la sala {code}…
      </div>
    );
  }

  const players = state.session.players;
  const me = state.me;
  const isHost = me.isHost;
  const enough = players.length >= 2;

  return (
    <main className="min-h-screen p-6 max-w-3xl mx-auto">
      <header className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="text-white/40 text-xs uppercase">Sala</p>
          <h2 className="text-3xl font-bold tracking-widest">{code}</h2>
        </div>
        <div className="flex items-center gap-2 text-sm text-white/60">
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: me.color }} />
          {me.username} {isHost && <span className="ml-1 text-accent">(host)</span>}
        </div>
      </header>

      {state.session.prompt && (
        <section className="card mb-4 border-accent/30 bg-accent/5">
          <p className="text-xs uppercase text-white/40">Prompt (todos dibujaréis lo mismo)</p>
          <p className="text-xl font-semibold mt-1">"{state.session.prompt}"</p>
        </section>
      )}

      <section className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-lg">Jugadores ({players.length}/{state.session.maxPlayers})</h3>
          {isHost && (
            <button disabled={!enough} onClick={startGame} className="btn-primary">
              {enough ? 'Iniciar partida' : `Necesitas ${2 - players.length} más`}
            </button>
          )}
        </div>

        <ul className="grid grid-cols-2 gap-3">
          {players.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2"
            >
              <span className="w-3 h-3 rounded-full" style={{ background: p.color }} />
              <span className="flex-1 truncate">{p.username}</span>
              {p.id === me.id && <span className="text-accent text-xs">TÚ</span>}
              {p.isHost && <span className="text-white/40 text-xs">HOST</span>}
              {!p.connected && <span className="text-white/30 text-xs">offline</span>}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-6 card text-sm text-white/60">
        <h4 className="text-white font-medium mb-2">Cómo se juega</h4>
        <ol className="list-decimal list-inside space-y-1">
          <li>Todos dibujáis el mismo prompt al mismo tiempo en lienzos independientes.</li>
          <li>Cuando pase el tiempo, los lienzos rotan: cada uno recibe el de otro jugador.</li>
          <li>Continúas dibujando sobre lo que ves, sin prompt nuevo. Se repite {players.length} veces.</li>
          <li>Todos los lienzos vuelven a sus autores y empieza la votación.</li>
          <li>Cada jugador vota <strong>una sola vez</strong> por su favorito.</li>
        </ol>
      </section>
    </main>
  );
}
